import type { ReactElement } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { MultilineTextSkeleton } from '@/components/ui/content-loading-skeletons';
import { Card, CardContent } from '@/components/ui/card';
import { patientQueries } from '@/features/patient-record/queries';

import { AllergiesCard } from './AllergiesCard';
import { MeasurementsCard } from './MeasurementsCard';
import { NarrativeCard } from './NarrativeCard';

interface MedicalHistorySectionProps {
  readonly guid: string;
  /** Patient DOB (full or partial ISO). Used to auto-compute
   *  `ageInMonths` on every measurement write — the BE requires it. */
  readonly dateOfBirth: string | null;
}

/**
 * Medical History composes three independent cards. Each card owns its own
 * loading / error envelope — a single failed query doesn't hide the
 * other cards, and the section anchors (`#narrative`, `#allergies`,
 * `#measurements`) stay in the DOM so the sub-tab navigation can still
 * scroll to them.
 *
 *   1. NarrativeCard    — two textareas with explicit Save / Discard.
 *   2. AllergiesCard    — NKDA toggle + allergy list with add/edit/delete.
 *   3. MeasurementsCard — pivot table (date × W/H/HC/AS), unit toggle,
 *                         multi-input add form.
 */
export function MedicalHistorySection({
  guid,
  dateOfBirth,
}: MedicalHistorySectionProps): ReactElement {
  const historyQuery = useQuery(patientQueries.medicalHistory(guid));
  const allergyProfileQuery = useQuery(patientQueries.allergyProfile(guid));
  const measurementsQuery = useQuery(patientQueries.measurements(guid));

  return (
    <section
      id="medical-history"
      aria-labelledby="medical-history-heading"
      className="scroll-mt-4 space-y-3"
    >
      <h2 id="medical-history-heading" className="sr-only">
        Medical history
      </h2>

      <CardSlot
        anchorId="narrative"
        query={historyQuery}
        errorHeadingKey="patient.medicalHistory.error.heading"
        errorMessageKey="patient.medicalHistory.error.message"
      >
        {(data) => <NarrativeCard guid={guid} history={data} />}
      </CardSlot>

      <CardSlot
        anchorId="allergies"
        query={allergyProfileQuery}
        errorHeadingKey="patient.medicalHistory.allergies.error.heading"
        errorMessageKey="patient.medicalHistory.allergies.error.message"
      >
        {(data) => <AllergiesCard guid={guid} profile={data} />}
      </CardSlot>

      <CardSlot
        anchorId="measurements"
        query={measurementsQuery}
        errorHeadingKey="patient.medicalHistory.measurementsError.heading"
        errorMessageKey="patient.medicalHistory.measurementsError.message"
      >
        {(data) => (
          <MeasurementsCard
            guid={guid}
            measurements={data}
            dateOfBirth={dateOfBirth}
          />
        )}
      </CardSlot>
    </section>
  );
}

interface CardSlotProps<T> {
  readonly anchorId: string;
  readonly query: UseQueryResult<T>;
  readonly errorHeadingKey: string;
  readonly errorMessageKey: string;
  readonly children: (data: T) => ReactElement;
}

/**
 * Wraps a card with its own loading/error envelope. The wrapper carries
 * the section anchor only while the child can't be rendered yet — once
 * the success branch mounts, the real card takes over the same `id`,
 * so the sub-tab navigator's `querySelector('#anchorId')` always finds
 * a scroll target and we never duplicate ids in the DOM.
 */
function CardSlot<T>({
  anchorId,
  query,
  errorHeadingKey,
  errorMessageKey,
  children,
}: CardSlotProps<T>): ReactElement {
  const { t } = useTranslation();
  if (query.isPending) {
    return (
      <div id={anchorId} className="scroll-mt-4">
        <Card>
          <CardContent className="py-6">
            <MultilineTextSkeleton ariaLabel={t('patient.medicalHistory.loading')} />
          </CardContent>
        </Card>
      </div>
    );
  }
  if (query.isError) {
    return (
      <div id={anchorId} className="scroll-mt-4">
        <Card>
          <CardContent className="py-6">
            <FetchErrorCallout
              className="max-w-none"
              error={query.error}
              heading={t(errorHeadingKey)}
              message={t(errorMessageKey)}
              codeLabel={t('routes.patient.error.codeLabel')}
              headingAs="p"
              actions={
                <Button
                  size="sm"
                  onClick={() => {
                    Promise.resolve(query.refetch()).catch(() => undefined);
                  }}
                >
                  {t('routes.patient.error.retry')}
                </Button>
              }
            />
          </CardContent>
        </Card>
      </div>
    );
  }
  return children(query.data);
}

